import { useMemo } from 'react'
import { clsx } from 'clsx'

interface RecordingWaveformProps {
  frequencyData: Uint8Array
  barCount?: number
  minHeight?: number
  maxHeight?: number
  className?: string
}

/**
 * Visual waveform component showing audio levels during recording.
 * Renders vertical bars that respond to frequency data.
 */
export function RecordingWaveform({
  frequencyData,
  barCount = 16,
  minHeight = 4,
  maxHeight = 24,
  className
}: RecordingWaveformProps) {
  // Resample frequency data to desired bar count
  const bars = useMemo(() => {
    if (frequencyData.length === 0) {
      return Array(barCount).fill(minHeight)
    }

    const step = Math.max(1, Math.floor(frequencyData.length / barCount))

    return Array.from({ length: barCount }, (_, i) => {
      // Average a few bins for smoother visualization
      let sum = 0
      let count = 0
      for (let j = 0; j < step && i * step + j < frequencyData.length; j++) {
        sum += frequencyData[i * step + j]
        count++
      }
      const value = count > 0 ? sum / count : 0

      // Map 0-255 to minHeight-maxHeight
      const normalized = value / 255
      return minHeight + normalized * (maxHeight - minHeight)
    })
  }, [frequencyData, barCount, minHeight, maxHeight])

  return (
    <div
      className={clsx(
        'flex items-center justify-center gap-[2px]',
        className
      )}
      style={{ height: `${maxHeight}px` }}
      aria-label="Audio level visualization"
      role="img"
    >
      {bars.map((height, i) => (
        <div
          key={i}
          className="w-[3px] bg-red-500 rounded-full transition-[height] duration-[50ms] ease-out"
          style={{ height: `${height}px` }}
        />
      ))}
    </div>
  )
}
